import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  TrendingUp,
  TrendingDown,
  Calendar as CalendarIcon,
  Filter,
  DollarSign,
  ArrowUp,
  ArrowDown,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Info,
  Wallet,
  Loader2,
  Upload
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { listarContasPagar } from "@/integrations/supabase/contas-pagar";
import { listarContasReceber } from "@/integrations/supabase/contas-receber";
import { listarContasBancarias } from "@/integrations/supabase/contas-bancarias";
import { listarFormasPagamento } from "@/integrations/supabase/formas-pagamento";
import { getEmpresaConfig, atualizarSaldoInicial } from "@/integrations/supabase/empresa-config";
import { supabase } from "@/integrations/supabase/client";
import { getTotaisFinanceiros, calcularSaldoAtual, calcularSaldoRealAtualAsync, agregarPorMes } from "@/utils/financialUtils";
import { formatCurrency } from "@/utils/formatCurrency";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import FluxoCaixaComparativo from "@/components/FluxoCaixaComparativo";
import { DialogImportarTransacoes } from "@/components/DialogImportarTransacoes";
import { DialogConfigurarContasEFormas } from "@/components/DialogConfigurarContasEFormas";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  addMonths,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subYears,
  startOfQuarter,
  endOfQuarter,
  startOfDay,
  endOfDay
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { useOnboardingHint } from "@/hooks/useOnboardingHint";

type PeriodoPredefinido = 
  | "hoje"
  | "ultimos_7_dias"
  | "ultimos_30_dias"
  | "mes_atual"
  | "mes_anterior"
  | "trimestre_atual"
  | "semestre_atual"
  | "ano_atual"
  | "customizado";

export default function FluxoCaixa() {
  const navigate = useNavigate();
  const hoje = new Date();
  const [activeTab, setActiveTab] = useState<"saldos" | "conta">("saldos");
  const [periodoPredefinido, setPeriodoPredefinido] = useState<PeriodoPredefinido>("mes_atual");
  const [dataRange, setDataRange] = useState<DateRange | undefined>({
    from: startOfMonth(hoje),
    to: endOfMonth(hoje)
  });
  const [filtroTipo, setFiltroTipo] = useState<string>("todos");
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [filtroConta, setFiltroConta] = useState<string>("todos");
  const [filtroForma, setFiltroForma] = useState<string>("todos");
  const [buscaTexto, setBuscaTexto] = useState<string>("");
  const [isSaldoModalOpen, setIsSaldoModalOpen] = useState(false);
  const [novoSaldoInicial, setNovoSaldoInicial] = useState<string>("");
  const [novaDataSaldo, setNovaDataSaldo] = useState<string>(new Date().toISOString().split('T')[0]);
  const [isSaldoSaving, setIsSaldoSaving] = useState(false);
  const [mesesMostra, setMesesMostra] = useState("12");
  const [isAjudaModalOpen, setIsAjudaModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isConfiguracaoModalOpen, setIsConfiguracaoModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showHint: showOnboardingHint, hideHint: dismissOnboardingHint } = useOnboardingHint("fluxo-caixa");

  // Função para redirecionar ao clicar em uma transação
  const handleClicarTransacao = (conta: any) => {
    if (conta.tipo === 'receber') {
      // Se tem pedido_id, ir para o pedido
      if (conta.pedido_id) {
        navigate(`/pedido-detalhe/${conta.pedido_id}`);
      } else {
        // Caso contrário, mostrar um toast (contas a receber não têm página própria)
        toast.info('Conta a Receber: ' + conta.descricao);
      }
    } else if (conta.tipo === 'pagar') {
      // Ir para contas a pagar (não temos página individual, então apenas mostra toast)
      toast.info('Conta a Pagar: ' + conta.descricao);
    }
  };

  // Calcular datas baseado no período selecionado
  const { dataInicio, dataFim } = useMemo(() => {
    const hoje = new Date();
    let inicio: Date;
    let fim: Date;

    switch (periodoPredefinido) {
      case "hoje":
        inicio = startOfDay(hoje);
        fim = endOfDay(hoje);
        break;
      case "ultimos_7_dias":
        inicio = startOfDay(subDays(hoje, 6));
        fim = endOfDay(hoje);
        break;
      case "ultimos_30_dias":
        inicio = startOfDay(subDays(hoje, 29));
        fim = endOfDay(hoje);
        break;
      case "mes_atual":
        inicio = startOfMonth(hoje);
        fim = endOfMonth(hoje);
        break;
      case "mes_anterior":
        inicio = startOfMonth(subMonths(hoje, 1));
        fim = endOfMonth(subMonths(hoje, 1));
        break;
      case "trimestre_atual":
        inicio = startOfQuarter(hoje);
        fim = endOfQuarter(hoje);
        break;
      case "semestre_atual":
        const semestre = Math.floor(hoje.getMonth() / 6);
        inicio = new Date(hoje.getFullYear(), semestre * 6, 1);
        fim = new Date(hoje.getFullYear(), (semestre + 1) * 6, 0, 23, 59, 59);
        break;
      case "ano_atual":
        inicio = startOfYear(hoje);
        fim = endOfYear(hoje);
        break;
      case "customizado":
        if (dataRange?.from && dataRange?.to) {
          inicio = startOfDay(dataRange.from);
          fim = endOfDay(dataRange.to);
        } else {
          inicio = startOfMonth(hoje);
          fim = endOfMonth(hoje);
        }
        break;
      default:
        inicio = startOfMonth(hoje);
        fim = endOfMonth(hoje);
    }

    // Garantir que as datas estão no início e fim do dia para evitar problemas de timezone
    const inicioFormatado = format(startOfDay(inicio), "yyyy-MM-dd");
    const fimFormatado = format(endOfDay(fim), "yyyy-MM-dd");
    
    return {
      dataInicio: inicioFormatado,
      dataFim: fimFormatado
    };
  }, [periodoPredefinido, dataRange]);

  const [erroContasReceber, setErroContasReceber] = useState<string | null>(null);
  const [erroContasPagar, setErroContasPagar] = useState<string | null>(null);

  const { data: contasPagar = [], isLoading: loadingPagar } = useQuery({
    queryKey: ["contas_pagar", "fluxo", dataInicio, dataFim],
    queryFn: async () => {
      try {
        setErroContasPagar(null);
        return await listarContasPagar({ data_inicio: dataInicio, data_fim: dataFim });
      } catch (err) {
        setErroContasPagar("Erro ao buscar contas a pagar. Tente novamente mais tarde.");
        console.error("[FLUXO_CAIXA] Erro ao buscar contas a pagar:", err);
        return [];
      }
    }
  });

  const { data: contasReceber = [], isLoading: loadingReceber } = useQuery({
    queryKey: ["contas_receber", "fluxo", dataInicio, dataFim],
    queryFn: async () => {
      try {
        setErroContasReceber(null);
        return await listarContasReceber({ data_inicio: dataInicio, data_fim: dataFim });
      } catch (err) {
        setErroContasReceber("Erro ao buscar contas a receber. Tente novamente mais tarde.");
        console.error("[FLUXO_CAIXA] Erro ao buscar contas a receber:", err);
        return [];
      }
    }
  });

  const isLoading = loadingPagar || loadingReceber;


  // Filtrar contas por status e busca
  const contasPagarFiltradas = useMemo(() => {
    let filtradas = contasPagar;

    // Filtro por status
    if (filtroStatus !== "todos") {
      filtradas = filtradas.filter(c => c.status === filtroStatus);
    }

    // Filtro por conta bancária
    if (filtroConta !== "todos") {
      filtradas = filtradas.filter(c => c.conta_bancaria_id === filtroConta);
    }

    // Filtro por forma de pagamento
    if (filtroForma !== "todos") {
      filtradas = filtradas.filter(c => c.forma_pagamento_id === filtroForma);
    }

    // Busca por texto
    if (buscaTexto.trim()) {
      const busca = buscaTexto.toLowerCase();
      filtradas = filtradas.filter(c =>
        c.descricao?.toLowerCase().includes(busca) ||
        c.observacoes?.toLowerCase().includes(busca) ||
        c.categoria?.toLowerCase().includes(busca)
      );
    }

    return filtradas;
  }, [contasPagar, filtroStatus, filtroConta, filtroForma, buscaTexto]);

  const contasReceberFiltradas = useMemo(() => {
    let filtradas = contasReceber;

    // Filtro por status
    if (filtroStatus !== "todos") {
      filtradas = filtradas.filter(c => c.status === filtroStatus);
    }

    // Filtro por conta bancária
    if (filtroConta !== "todos") {
      filtradas = filtradas.filter(c => c.conta_bancaria_id === filtroConta);
    }

    // Filtro por forma de pagamento
    if (filtroForma !== "todos") {
      filtradas = filtradas.filter(c => c.forma_pagamento_id === filtroForma);
    }

    // Busca por texto
    if (buscaTexto.trim()) {
      const busca = buscaTexto.toLowerCase();
      filtradas = filtradas.filter(c =>
        c.descricao?.toLowerCase().includes(busca) ||
        c.observacoes?.toLowerCase().includes(busca) ||
        c.categoria?.toLowerCase().includes(busca)
      );
    }

    return filtradas;
  }, [contasReceber, filtroStatus, filtroConta, filtroForma, buscaTexto]);

  // Buscar TODAS as contas a pagar realizadas (sem filtro de período) para calcular saldo atual
  const { data: contasPagarTodas = [] } = useQuery({
    queryKey: ["contas_pagar_todas"],
    queryFn: async () => {
      try {
        return await listarContasPagar({ data_inicio: null, data_fim: null });
      } catch (err) {
        console.error("[FLUXO_CAIXA] Erro ao buscar todas as contas a pagar:", err);
        return [];
      }
    }
  });

  // Buscar TODAS as contas a receber realizadas (sem filtro de período) para calcular saldo atual
  const { data: contasReceberTodas = [] } = useQuery({
    queryKey: ["contas_receber_todas"],
    queryFn: async () => {
      try {
        return await listarContasReceber({ data_inicio: null, data_fim: null });
      } catch (err) {
        console.error("[FLUXO_CAIXA] Erro ao buscar todas as contas a receber:", err);
        return [];
      }
    }
  });

  // Buscar totais financeiros usando função compartilhada
  const { data: totaisFinanceiros, error: totaisError } = useQuery({
    queryKey: ["totaisFinanceiros", dataInicio, dataFim],
    queryFn: () => getTotaisFinanceiros({ startDate: dataInicio, endDate: dataFim }),
    staleTime: 10000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: 30000,
  });

  // Buscar configuração da empresa (saldo inicial)
  const { data: empresaConfig } = useQuery({
    queryKey: ["empresa_config"],
    queryFn: async () => {
      const empresaId = await getCurrentEmpresaId();
      if (!empresaId) return null;
      return await getEmpresaConfig(empresaId);
    }
  });

  // Buscar pedidos para gráfico comparativo (todo o período)
  const { data: orders = [] } = useQuery({
    queryKey: ["atelie_orders_all"],
    queryFn: async () => {
      const empresaId = await getCurrentEmpresaId();
      if (!empresaId) return [];

      const { data, error } = await supabase
        .from('atelie_orders')
        .select('id, code, value, paid, created_at, discount, discount_type')
        .eq('empresa_id', empresaId)
        .neq('status', 'Cancelado');

      return error ? [] : (data || []);
    }
  });

  // Buscar receitas para gráfico comparativo (todo o período)
  const { data: receitas = [] } = useQuery({
    queryKey: ["atelie_receitas_all"],
    queryFn: async () => {
      const empresaId = await getCurrentEmpresaId();
      if (!empresaId) return [];

      const { data, error } = await supabase
        .from('atelie_receitas')
        .select('id, order_code, amount, payment_date, status')
        .eq('empresa_id', empresaId);

      return error ? [] : (data || []);
    }
  });

  // Buscar contas bancárias para mostrar saldo por conta
  const { data: contasBancarias = [] } = useQuery({
    queryKey: ["contas_bancarias"],
    queryFn: listarContasBancarias
  });

  // Buscar formas de pagamento para mostrar totais por forma
  const { data: formasPagamento = [] } = useQuery({
    queryKey: ["formas_pagamento"],
    queryFn: listarFormasPagamento
  });

  // Tratamento de erro para garantir que o sistema não quebre
  if (totaisError) {
    console.error("Erro ao calcular totais financeiros:", totaisError);
  }

  // Garantir que os valores nunca sejam negativos ou NaN
  const totalPagar = Math.max(0, Number(totaisFinanceiros?.totalPagar) || 0);
  const totalReceber = Math.max(0, Number(totaisFinanceiros?.totalReceber) || 0);
  const saldo = Number(totaisFinanceiros?.saldo) || 0;

  // Calcular saldo atual da conta (usando função oficial única)
  const [saldoAtualCalc, setSaldoAtualCalc] = useState<ReturnType<typeof calcularSaldoAtual>>({
    saldoInicial: 0,
    dataSaldoInicial: new Date().toISOString().split('T')[0],
    receitasRealizadas: 0,
    despesasRealizadas: 0,
    saldoAtual: 0,
  });

  useEffect(() => {
    const buscarSaldo = async () => {
      const saldo = await calcularSaldoRealAtualAsync();
      setSaldoAtualCalc(saldo);
    };
    buscarSaldo();
    const interval = setInterval(buscarSaldo, 30000); // Atualizar a cada 30s
    return () => clearInterval(interval);
  }, []);

  // Calcular saldo do período (apenas do período filtrado)
  const saldoPeriodoCalc = useMemo(() => {
    if (!empresaConfig) {
      return calcularSaldoAtual(0, contasPagar, contasReceber);
    }
    return calcularSaldoAtual(empresaConfig.saldo_inicial || 0, contasPagar, contasReceber);
  }, [empresaConfig, contasPagar, contasReceber]);

  // Agregação de dados por mês para gráfico comparativo (inclui contas manuais + receitas de pedidos)
  const dadosComparativo = useMemo(() => {
    return agregarPorMes(contasPagar, contasReceber, parseInt(mesesMostra), orders, receitas);
  }, [contasPagar, contasReceber, mesesMostra, orders, receitas]);

  // Calcular saldo de cada conta bancária
  const saldosPorConta = useMemo(() => {
    return contasBancarias.map(conta => {
      const contasReceberDaConta = contasReceber.filter(c =>
        c.conta_bancaria_id === conta.id && c.status === 'recebido'
      );
      const contasPagarDaConta = contasPagar.filter(c =>
        c.conta_bancaria_id === conta.id && c.status === 'pago'
      );

      const receitasRealizadas = contasReceberDaConta.reduce((acc, c) =>
        acc + (c.valor_recebido || 0), 0
      );
      const despesasRealizadas = contasPagarDaConta.reduce((acc, c) =>
        acc + (c.valor_pago || 0), 0
      );

      const saldoAtual = (conta.saldo_inicial || 0) + receitasRealizadas - despesasRealizadas;

      return {
        ...conta,
        receitasRealizadas,
        despesasRealizadas,
        saldoAtual
      };
    });
  }, [contasBancarias, contasReceber, contasPagar]);

  // Calcular totais por forma de pagamento
  const totaisPorForma = useMemo(() => {
    return formasPagamento.map(forma => {
      const contasReceberPorForma = contasReceber.filter(c =>
        c.forma_pagamento_id === forma.id && c.status === 'recebido'
      );
      const contasPagarPorForma = contasPagar.filter(c =>
        c.forma_pagamento_id === forma.id && c.status === 'pago'
      );

      const totalRecebido = contasReceberPorForma.reduce((acc, c) =>
        acc + (c.valor_recebido || 0), 0
      );
      const totalPago = contasPagarPorForma.reduce((acc, c) =>
        acc + (c.valor_pago || 0), 0
      );

      return {
        ...forma,
        totalRecebido,
        totalPago,
        saldo: totalRecebido - totalPago
      };
    });
  }, [formasPagamento, contasReceber, contasPagar]);

  // Handler para salvar saldo inicial
  const handleSalvarSaldo = async () => {
    if (!novoSaldoInicial || !novaDataSaldo) {
      toast.error("Preencha todos os campos");
      return;
    }

    setIsSaldoSaving(true);
    try {
      const empresaId = await getCurrentEmpresaId();
      if (!empresaId) {
        toast.error("Usuário não tem empresa associada");
        return;
      }

      const result = await atualizarSaldoInicial(
        empresaId,
        parseFloat(novoSaldoInicial),
        novaDataSaldo
      );

      if (result.ok) {
        toast.success("Saldo inicial atualizado com sucesso!");
        queryClient.invalidateQueries({ queryKey: ["empresa_config"] });
        setIsSaldoModalOpen(false);
        setNovoSaldoInicial("");
      } else {
        toast.error(result.error || "Erro ao salvar saldo inicial");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao salvar saldo inicial");
    } finally {
      setIsSaldoSaving(false);
    }
  };

  // Recalcular totais com contas filtradas (para exibição detalhada por dia)
  const calculosFiltrados = useMemo(() => {
    // Usar totais da função compartilhada para consistência
  // ...existing code...
  // Os valores já estão padronizados acima

    // Agrupar por dia com detalhes das contas
    const movimentacoesPorDia: Record<string, { pagar: number; receber: number; contasPagar: any[]; contasReceber: any[] }> = {};

    contasPagarFiltradas.forEach(conta => {
      // Para contas pagas, usar data_pagamento se disponível, senão usar data_vencimento
      // Para contas pendentes/atrasadas, usar data_vencimento
      const data = (conta.status === 'pago' && conta.data_pagamento)
        ? conta.data_pagamento.split('T')[0]
        : conta.data_vencimento.split('T')[0];

      if (!movimentacoesPorDia[data]) {
        movimentacoesPorDia[data] = { pagar: 0, receber: 0, contasPagar: [], contasReceber: [] };
      }

      // Calcular valor baseado no status
      let valor = 0;
      if (filtroStatus === "todos") {
        // Corrigir inconsistência: se status é "pago" mas valor_pago = 0, tratar como pendente
        if (conta.status === 'pago' && conta.valor_pago === 0) {
          valor = conta.valor_total; // Tratar como pendente
        } else if (conta.status === 'pendente' || conta.status === 'atrasado') {
          valor = conta.valor_total - conta.valor_pago; // Valor pendente
        } else if (conta.status === 'pago') {
          valor = conta.valor_pago; // Valor já pago
        }
      } else if (filtroStatus === "pago") {
        if (conta.status === 'pago') {
          valor = conta.valor_pago;
        }
      } else if (filtroStatus === "recebido") {
        // Não se aplica a contas a pagar
      } else {
        // Pendente ou atrasado (ou pago com valor_pago = 0)
        if (conta.status === 'pendente' || conta.status === 'atrasado') {
          valor = conta.valor_total - conta.valor_pago;
        } else if (conta.status === 'pago' && conta.valor_pago === 0) {
          valor = conta.valor_total; // Tratar como pendente
        }
      }

      if (valor > 0) {
        movimentacoesPorDia[data].pagar += valor;
        movimentacoesPorDia[data].contasPagar.push({
          ...conta,
          valor_exibido: valor,
          tipo: 'pagar'
        });
      }
    });

    contasReceberFiltradas.forEach(conta => {
      // Para contas recebidas, usar data_recebimento se disponível, senão usar data_vencimento
      // Para contas pendentes/atrasadas, usar data_vencimento
      const data = (conta.status === 'recebido' && conta.data_recebimento)
        ? conta.data_recebimento.split('T')[0]
        : conta.data_vencimento.split('T')[0];

      if (!movimentacoesPorDia[data]) {
        movimentacoesPorDia[data] = { pagar: 0, receber: 0, contasPagar: [], contasReceber: [] };
      }

      // Calcular valor baseado no status
      let valor = 0;
      if (filtroStatus === "todos") {
        // Corrigir inconsistência: se status é "recebido" mas valor_recebido = 0, tratar como pendente
        if (conta.status === 'recebido' && conta.valor_recebido === 0) {
          valor = conta.valor_total; // Tratar como pendente
        } else if (conta.status === 'pendente' || conta.status === 'atrasado') {
          valor = conta.valor_total - conta.valor_recebido; // Valor pendente
        } else if (conta.status === 'recebido') {
          valor = conta.valor_recebido; // Valor já recebido
        }
      } else if (filtroStatus === "recebido") {
        if (conta.status === 'recebido') {
          valor = conta.valor_recebido;
        }
      } else if (filtroStatus === "pago") {
        // Não se aplica a contas a receber
      } else {
        // Pendente ou atrasado (ou recebido com valor_recebido = 0)
        if (conta.status === 'pendente' || conta.status === 'atrasado') {
          valor = conta.valor_total - conta.valor_recebido;
        } else if (conta.status === 'recebido' && conta.valor_recebido === 0) {
          valor = conta.valor_total; // Tratar como pendente
        }
      }

      if (valor > 0) {
        movimentacoesPorDia[data].receber += valor;
        movimentacoesPorDia[data].contasReceber.push({
          ...conta,
          valor_exibido: valor,
          tipo: 'receber'
        });
      }
    });

    const diasOrdenados = Object.keys(movimentacoesPorDia).sort();

    return {
      totalPagar,
      totalReceber,
      saldo,
      movimentacoesPorDia,
      diasOrdenados
    };
  }, [contasPagarFiltradas, contasReceberFiltradas]);

  const movimentacoesFiltradas = useMemo(() => {
    if (filtroTipo === "todos") {
      return calculosFiltrados.diasOrdenados;
    }
    return calculosFiltrados.diasOrdenados.filter(dia => {
      const mov = calculosFiltrados.movimentacoesPorDia[dia];
      if (filtroTipo === "receber") return mov.receber > 0;
      if (filtroTipo === "pagar") return mov.pagar > 0;
      return true;
    });
  }, [calculosFiltrados, filtroTipo]);

  // Atualizar dataRange quando período predefinido muda
  const handlePeriodoChange = (novoPeriodo: PeriodoPredefinido) => {
    setPeriodoPredefinido(novoPeriodo);
    if (novoPeriodo !== "customizado") {
      const hoje = new Date();
      let inicio: Date;
      let fim: Date;

      switch (novoPeriodo) {
        case "hoje":
          inicio = startOfDay(hoje);
          fim = endOfDay(hoje);
          break;
        case "ultimos_7_dias":
          inicio = startOfDay(subDays(hoje, 6));
          fim = endOfDay(hoje);
          break;
        case "ultimos_30_dias":
          inicio = startOfDay(subDays(hoje, 29));
          fim = endOfDay(hoje);
          break;
        case "mes_atual":
          inicio = startOfMonth(hoje);
          fim = endOfMonth(hoje);
          break;
        case "mes_anterior":
          inicio = startOfMonth(subMonths(hoje, 1));
          fim = endOfMonth(subMonths(hoje, 1));
          break;
        case "trimestre_atual":
          inicio = startOfQuarter(hoje);
          fim = endOfQuarter(hoje);
          break;
        case "semestre_atual":
          const semestre = Math.floor(hoje.getMonth() / 6);
          inicio = new Date(hoje.getFullYear(), semestre * 6, 1);
          fim = new Date(hoje.getFullYear(), (semestre + 1) * 6, 0, 23, 59, 59);
          break;
        case "ano_atual":
          inicio = startOfYear(hoje);
          fim = endOfYear(hoje);
          break;
        default:
          inicio = startOfMonth(hoje);
          fim = endOfMonth(hoje);
      }
      setDataRange({ from: inicio, to: fim });
    }
  };

  const limparFiltros = () => {
    setFiltroTipo("todos");
    setFiltroStatus("todos");
    setFiltroConta("todos");
    setFiltroForma("todos");
    setBuscaTexto("");
    setPeriodoPredefinido("mes_atual");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <PageHeader
        title="Fluxo de Caixa"
        icon={<Wallet className="w-5 h-5 text-purple-600" />}
        rightActions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfiguracaoModalOpen(true)}
              className="gap-1 md:gap-2 border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 text-xs md:text-sm px-2 md:px-3 py-1.5"
              title="Configurar contas e formas de pagamento"
            >
              ⚙️
              <span className="hidden sm:inline">Configurar</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsImportModalOpen(true)}
              className="gap-1 md:gap-2 border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 text-xs md:text-sm px-2 md:px-3 py-1.5"
              title="Importar transações de OFX ou Excel"
            >
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Importar</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAjudaModalOpen(true)}
              title="Ver guia de ajuda"
              className="gap-1 md:gap-2 border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 text-xs md:text-sm px-2 md:px-3 py-1.5"
            >
              <Info className="h-4 w-4" />
              <span className="hidden sm:inline">Ajuda</span>
            </Button>
          </div>
        }
      />

      <div className="container mx-auto p-4 md:p-6 max-w-6xl space-y-4 md:space-y-6">
        {/* Card de Dicas */}
        {showOnboardingHint && (
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-gray-200 shadow-sm animate-fade-in">
            <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 px-4 md:px-5 py-3">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-purple-600" />
                <CardTitle className="text-sm font-semibold text-gray-900">
                  Cuide do fluxo de caixa para não ser pega de surpresa
                </CardTitle>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={dismissOnboardingHint}
                aria-label="Fechar dica de fluxo de caixa"
              >
                <X className="w-4 h-4" />
              </button>
            </CardHeader>
            <CardContent className="pt-0 text-xs md:text-sm text-gray-700 space-y-1">
              <p>
                Aqui você vê o que tem para <strong>receber</strong> e <strong>pagar</strong> em cada período.
                Isso te ajuda a planejar o mês e evitar apertos com contas inesperadas.
              </p>
              <p>
                Um hábito poderoso é olhar esse resumo pelo menos 1 vez por semana e sempre que for tomar decisões
                como comprar material, dar desconto ou aceitar um pedido grande.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Filtros Compactados */}
        <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden">
          <CardHeader className="bg-purple-50 border-b border-gray-200 px-4 md:px-5 py-3">
            <CardTitle className="text-base md:text-lg text-gray-900 flex items-center gap-2">
              <Filter className="h-5 w-5 text-purple-600" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-5">
            {(filtroTipo !== "todos" || filtroStatus !== "todos" || filtroConta !== "todos" || filtroForma !== "todos" || buscaTexto.trim() || periodoPredefinido !== "mes_atual") && (
              <div className="mb-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={limparFiltros}
                  className="text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7"
                >
                  <X className="h-3 w-3 mr-1" />
                  Limpar Filtros
                </Button>
              </div>
            )}
            {/* Grid Compacto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
              {/* Período */}
              <Select value={periodoPredefinido} onValueChange={(value) => handlePeriodoChange(value as PeriodoPredefinido)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoje">Hoje</SelectItem>
                  <SelectItem value="ultimos_7_dias">Últimos 7 dias</SelectItem>
                  <SelectItem value="ultimos_30_dias">Últimos 30 dias</SelectItem>
                  <SelectItem value="mes_atual">Mês Atual</SelectItem>
                  <SelectItem value="mes_anterior">Mês Anterior</SelectItem>
                  <SelectItem value="trimestre_atual">Trimestre Atual</SelectItem>
                  <SelectItem value="ano_atual">Ano Atual</SelectItem>
                  <SelectItem value="customizado">Customizado</SelectItem>
                </SelectContent>
              </Select>

              {/* Tipo */}
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="receber">Receitas</SelectItem>
                  <SelectItem value="pagar">Despesas</SelectItem>
                </SelectContent>
              </Select>

              {/* Status */}
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="atrasado">Atrasado</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="recebido">Recebido</SelectItem>
                </SelectContent>
              </Select>

              {/* Conta */}
              <Select value={filtroConta} onValueChange={setFiltroConta}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Conta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {contasBancarias.map(conta => (
                    <SelectItem key={conta.id} value={conta.id || ''}>
                      {conta.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Forma de Pagamento */}
              <Select value={filtroForma} onValueChange={setFiltroForma}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Forma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {formasPagamento.map(forma => (
                    <SelectItem key={forma.id} value={forma.id || ''}>
                      {forma.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Busca */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar..."
                  value={buscaTexto}
                  onChange={(e) => setBuscaTexto(e.target.value)}
                  className="h-9 text-sm pl-9 pr-8"
                />
                {buscaTexto && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-0.5 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setBuscaTexto("")}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Período Customizado */}
            {periodoPredefinido === "customizado" && (
              <div className="mt-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-9 justify-start text-sm">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dataRange?.from ? (
                        dataRange.to ? (
                          `${format(dataRange.from, "dd/MM/yyyy", { locale: ptBR })} - ${format(dataRange.to, "dd/MM/yyyy", { locale: ptBR })}`
                        ) : (
                          format(dataRange.from, "dd/MM/yyyy", { locale: ptBR })
                        )
                      ) : (
                        "Selecione o período"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={dataRange?.from ? new Date(dataRange.from.getFullYear(), dataRange.from.getMonth(), 1) : new Date()}
                      selected={dataRange}
                      onSelect={(range) => {
                        if (range?.from && range?.to) {
                          setDataRange({ from: startOfDay(range.from), to: endOfDay(range.to) });
                        } else if (range?.from) {
                          setDataRange({ from: startOfDay(range.from), to: undefined });
                        } else {
                          setDataRange(range);
                        }
                      }}
                      numberOfMonths={2}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Abas de Visualização */}
        <div className="flex gap-2 border-b border-gray-200">
          <Button
            variant={activeTab === "saldos" ? "default" : "ghost"}
            onClick={() => setActiveTab("saldos")}
            className={activeTab === "saldos" ? "border-b-2 border-purple-600" : ""}
          >
            Saldos
          </Button>
          <Button
            variant={activeTab === "conta" ? "default" : "ghost"}
            onClick={() => setActiveTab("conta")}
            className={activeTab === "conta" ? "border-b-2 border-purple-600" : ""}
          >
            Saldo por Conta
          </Button>
        </div>

        {/* Cards de Resumo - Aba Saldos */}
        {activeTab === "saldos" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card className="bg-gradient-to-br from-white to-blue-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden cursor-pointer" onClick={() => { setNovoSaldoInicial((empresaConfig?.saldo_inicial || 0).toString()); setNovaDataSaldo(empresaConfig?.data_saldo_inicial || new Date().toISOString().split('T')[0]); setIsSaldoModalOpen(true); }}>
            <CardHeader className="bg-blue-50 border-b border-gray-200 px-4 md:px-5 py-3">
              <CardTitle className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-blue-600" />
                Saldo Atual
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-5">
              <div className={`text-lg md:text-2xl font-bold ${saldoAtualCalc.saldoAtual >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {formatCurrency({ value: saldoAtualCalc.saldoAtual, currency: 'BRL' })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Saldo real (todo período)</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-purple-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.1s'}}>
            <CardHeader className="bg-purple-50 border-b border-gray-200 px-4 md:px-5 py-3">
              <CardTitle className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-purple-600" />
                Saldo do Período
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-5">
              <div className={`text-lg md:text-2xl font-bold ${saldoPeriodoCalc.saldoAtual >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                {formatCurrency({ value: saldoPeriodoCalc.saldoAtual, currency: 'BRL' })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {dataInicio === dataFim ? 'Hoje' : `${dataInicio.split('-').reverse().join('/')} até ${dataFim.split('-').reverse().join('/')}`}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-red-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.2s'}}>
            <CardHeader className="bg-red-50 border-b border-gray-200 px-4 md:px-5 py-3">
              <CardTitle className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                <ArrowDown className="h-5 w-5 text-red-600" />
                Total a Pagar
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-5">
              <div className="text-lg md:text-2xl font-bold text-red-600">
                {formatCurrency({ value: totalPagar, currency: 'BRL' })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {filtroStatus === "todos"
                  ? "Pendentes e atrasadas"
                  : filtroStatus === "pago"
                    ? "Contas pagas"
                    : filtroStatus === "recebido"
                      ? "Contas recebidas"
                      : filtroStatus === "pendente"
                        ? "Contas pendentes"
                        : filtroStatus === "atrasado"
                          ? "Contas atrasadas"
                          : "Contas pendentes e atrasadas"}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-green-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.3s'}}>
            <CardHeader className="bg-green-50 border-b border-gray-200 px-4 md:px-5 py-3">
              <CardTitle className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                <ArrowUp className="h-5 w-5 text-green-600" />
                Total a Receber
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-5">
              {erroContasReceber ? (
                <>
                  <div className="text-lg md:text-2xl font-bold text-green-600">--</div>
                  <p className="text-xs text-red-600 mt-1">{erroContasReceber}</p>
                </>
              ) : (
                <>
                  <div className="text-lg md:text-2xl font-bold text-green-600">
                    {formatCurrency({ value: totalReceber, currency: 'BRL' })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {filtroStatus === "todos" 
                      ? "Pendentes e atrasadas" 
                      : filtroStatus === "recebido" 
                        ? "Contas recebidas" 
                        : filtroStatus === "pago"
                          ? "Contas pagas"
                          : filtroStatus === "pendente"
                            ? "Contas pendentes"
                            : filtroStatus === "atrasado"
                              ? "Contas atrasadas"
                              : "Contas pendentes e atrasadas"}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-amber-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: '0.4s'}}>
            <CardHeader className="bg-amber-50 border-b border-gray-200 px-4 md:px-5 py-3">
              <CardTitle className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-600" />
                Saldo Previsto
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-5">
              <div className={`text-lg md:text-2xl font-bold ${(totaisFinanceiros?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency({ value: saldo, currency: 'BRL' })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {saldo >= 0 ? 'Saldo positivo' : 'Saldo negativo'}
              </p>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Saldo por Conta Bancária - Aba Conta */}
        {activeTab === "conta" && contasBancarias.length > 0 && (
          <div className="space-y-3 md:space-y-4">
            <h3 className="text-base md:text-lg font-semibold text-gray-900">Saldo por Conta</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {saldosPorConta.map((conta, idx) => (
                <Card key={conta.id} className="bg-gradient-to-br from-white to-blue-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in overflow-hidden" style={{animationDelay: `${0.1 * idx}s`}}>
                  <CardHeader className="bg-blue-50 border-b border-gray-200 px-4 md:px-5 py-3">
                    <CardTitle className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                      {conta.nome}
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-600 mt-0.5">
                      {conta.tipo && `${conta.tipo.charAt(0).toUpperCase() + conta.tipo.slice(1)}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 md:p-5 space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Saldo Atual</p>
                      <p className={`text-2xl font-bold ${conta.saldoAtual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency({ value: conta.saldoAtual, currency: 'BRL' })}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t text-xs">
                      <div>
                        <p className="text-muted-foreground">Inicial</p>
                        <p className="font-semibold">
                          {formatCurrency({ value: conta.saldo_inicial || 0, currency: 'BRL' })}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Realizado</p>
                        <p className="font-semibold text-green-600">
                          +{formatCurrency({ value: conta.receitasRealizadas, currency: 'BRL' })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Totais por Forma de Pagamento - Aba Conta */}
        {activeTab === "conta" && formasPagamento.length > 0 && totaisPorForma.some(f => f.totalRecebido > 0 || f.totalPago > 0) && (
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Totais por Forma de Pagamento</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {totaisPorForma.filter(f => f.totalRecebido > 0 || f.totalPago > 0).map(forma => (
                <Card key={forma.id} className="bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      {forma.nome}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Saldo</p>
                      <p className={`text-2xl font-bold ${forma.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency({ value: forma.saldo, currency: 'BRL' })}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t text-xs">
                      <div>
                        <p className="text-muted-foreground">Recebido</p>
                        <p className="font-semibold text-green-600">
                          +{formatCurrency({ value: forma.totalRecebido, currency: 'BRL' })}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Pago</p>
                        <p className="font-semibold text-red-600">
                          -{formatCurrency({ value: forma.totalPago, currency: 'BRL' })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Gráfico de Fluxo */}
        {isLoading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Carregando dados...</p>
            </CardContent>
          </Card>
        ) : movimentacoesFiltradas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhuma movimentação no período</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Fluxo de Caixa por Dia</CardTitle>
              <CardDescription>
                {(() => {
                  // Parsear a data corretamente para evitar problemas de timezone
                  const [anoInicio, mesInicio, diaInicio] = dataInicio.split('-').map(Number);
                  const [anoFim, mesFim, diaFim] = dataFim.split('-').map(Number);
                  const inicio = new Date(anoInicio, mesInicio - 1, diaInicio);
                  const fim = new Date(anoFim, mesFim - 1, diaFim);
                  return `Movimentações previstas de ${format(inicio, "dd/MM/yyyy", { locale: ptBR })} até ${format(fim, "dd/MM/yyyy", { locale: ptBR })}`;
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {movimentacoesFiltradas.map((dia) => {
                  const mov = calculosFiltrados.movimentacoesPorDia[dia];
                  const saldoDia = mov.receber - mov.pagar;

                  return (
                    <div key={dia} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-semibold">
                            {(() => {
                              // Parsear a data corretamente para evitar problemas de timezone
                              const [ano, mes, diaNum] = dia.split('-').map(Number);
                              const data = new Date(ano, mes - 1, diaNum);
                              return format(data, "dd/MM/yyyy", { locale: ptBR });
                            })()}
                          </span>
                        </div>
                        <Badge variant={saldoDia >= 0 ? "default" : "destructive"}>
                          Saldo: {formatCurrency({ value: saldoDia, currency: 'BRL' })}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3 pb-3 border-b">
                        {mov.receber > 0 && (
                          <div className="flex items-center gap-2 text-green-600">
                            <TrendingUp className="h-4 w-4" />
                            <span>
                              {filtroStatus === "recebido" ? "Recebido" : "Receber"}: {formatCurrency({ value: mov.receber, currency: 'BRL' })}
                            </span>
                          </div>
                        )}
                        {mov.pagar > 0 && (
                          <div className="flex items-center gap-2 text-red-600">
                            <TrendingDown className="h-4 w-4" />
                            <span>
                              {filtroStatus === "pago" ? "Pago" : "Pagar"}: {formatCurrency({ value: mov.pagar, currency: 'BRL' })}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Detalhes das contas receber */}
                      {mov.contasReceber && mov.contasReceber.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {mov.contasReceber.map((conta, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleClicarTransacao(conta)}
                              className="text-xs bg-green-50 p-2 rounded border-l-2 border-green-500 cursor-pointer hover:bg-green-100 transition-colors"
                              title="Clique para abrir detalhes"
                            >
                              <div className="font-medium text-green-700">{conta.descricao}</div>
                              <div className="text-green-600 font-semibold">{formatCurrency({ value: conta.valor_exibido, currency: 'BRL' })}</div>
                              {conta.pedido_id && <div className="text-xs text-green-500 mt-1">📋 Pedido</div>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Detalhes das contas pagar */}
                      {mov.contasPagar && mov.contasPagar.length > 0 && (
                        <div className="space-y-2">
                          {mov.contasPagar.map((conta, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleClicarTransacao(conta)}
                              className="text-xs bg-red-50 p-2 rounded border-l-2 border-red-500 cursor-pointer hover:bg-red-100 transition-colors"
                              title="Clique para abrir detalhes"
                            >
                              <div className="font-medium text-red-700">{conta.descricao}</div>
                              <div className="text-red-600 font-semibold">{formatCurrency({ value: conta.valor_exibido, currency: 'BRL' })}</div>
                              <div className="text-xs text-red-500 mt-1">📌 Conta a Pagar</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Seção Comparativo de Meses */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Comparativo de Meses</CardTitle>
                <CardDescription>Análise de receitas vs despesas</CardDescription>
              </div>
              <Select value={mesesMostra} onValueChange={setMesesMostra}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Últimos 6 meses</SelectItem>
                  <SelectItem value="12">Últimos 12 meses</SelectItem>
                  <SelectItem value="24">Últimos 24 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <FluxoCaixaComparativo dados={dadosComparativo} isLoading={isLoading} />
          </CardContent>
        </Card>
      </div>

      {/* Modal de Edição de Saldo Inicial */}
      <Dialog open={isSaldoModalOpen} onOpenChange={setIsSaldoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Saldo Inicial</DialogTitle>
            <DialogDescription>
              Insira o saldo inicial da sua conta bancária para reconciliação
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="data-saldo">Data do Saldo</Label>
              <Input
                id="data-saldo"
                type="date"
                value={novaDataSaldo}
                onChange={(e) => setNovaDataSaldo(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="saldo-inicial">Saldo Inicial (R$)</Label>
              <Input
                id="saldo-inicial"
                type="number"
                step="0.01"
                min="0"
                value={novoSaldoInicial}
                onChange={(e) => setNovoSaldoInicial(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSaldoModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSalvarSaldo}
              disabled={isSaldoSaving}
            >
              {isSaldoSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Ajuda */}
      <Dialog open={isAjudaModalOpen} onOpenChange={setIsAjudaModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              Guia de Ajuda - Fluxo de Caixa
            </DialogTitle>
            <DialogDescription>
              Aprenda como usar cada funcionalidade desta página
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Abas */}
            <div className="border-l-4 border-purple-600 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">📑 Abas de Visualização</h3>
              <p className="text-sm text-muted-foreground">
                A página tem duas abas para organizar melhor os dados:
              </p>
              <div className="bg-purple-50 p-3 rounded text-sm space-y-2">
                <p><strong>1. Aba "Saldos"</strong> - Visão Resumida</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Saldo Atual (total real)</li>
                  <li>Saldo do Período (apenas do período selecionado)</li>
                  <li>Total a Pagar e Total a Receber</li>
                  <li>Saldo Previsto</li>
                </ul>
                <p className="mt-2"><strong>2. Aba "Saldo por Conta"</strong> - Visão Detalhada</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Saldo em cada conta bancária</li>
                  <li>Totais por forma de pagamento (PIX, Cartão, etc)</li>
                  <li>Rastreamento de onde está seu dinheiro</li>
                </ul>
              </div>
            </div>

            {/* Card de Dicas */}
            <div className="border-l-4 border-yellow-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">💡 Card de Dicas (Topo)</h3>
              <p className="text-sm text-muted-foreground">
                Aparece no topo da página para orientar novos usuários.
              </p>
              <div className="bg-yellow-50 p-3 rounded text-sm space-y-1">
                <p>Explica o propósito do Fluxo de Caixa e oferece dicas práticas:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>O que você pode fazer nesta página</li>
                  <li>Por que é importante revisar regularmente</li>
                  <li>Quando usar para tomar decisões</li>
                </ul>
                <p className="text-xs mt-2">Clique no ✕ para descartar e não ver novamente</p>
              </div>
            </div>

            {/* Filtros Compactados */}
            <div className="border-l-4 border-orange-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">🔍 Filtros Compactados</h3>
              <p className="text-sm text-muted-foreground">
                Uma linha com todos os filtros principais para melhor organização.
              </p>
              <div className="bg-orange-50 p-3 rounded text-sm space-y-1">
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Período:</strong> Hoje, 7 dias, 30 dias, mês, trimestre, ano ou customizado</li>
                  <li><strong>Tipo:</strong> Todos, Receitas ou Despesas</li>
                  <li><strong>Status:</strong> Pendente, Pago/Recebido, Atrasado ou Todos</li>
                  <li><strong>Conta:</strong> Selecione qual conta bancária visualizar</li>
                  <li><strong>Forma:</strong> Filtre por forma de pagamento (PIX, Cartão, etc)</li>
                  <li><strong>Busca:</strong> Procure por descrição, categoria ou observações</li>
                </ul>
                <p className="text-xs mt-2">Clique em "Limpar" para resetar todos os filtros</p>
              </div>
            </div>

            {/* Card 1 */}
            <div className="border-l-4 border-blue-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">1️⃣ Saldo Atual da Conta</h3>
              <p className="text-sm text-muted-foreground">
                Este card mostra seu saldo em tempo real baseado no saldo inicial configurado.
              </p>
              <div className="bg-blue-50 p-3 rounded text-sm space-y-1">
                <p><strong>Como usar:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Clique no card azul para abrir a configuração</li>
                  <li>Insira a data do seu saldo inicial (ex: 01/06/2026)</li>
                  <li>Insira o saldo que você tinha nessa data (ex: R$ 10.000)</li>
                  <li>O saldo será atualizado automaticamente quando houver receitas/pagamentos</li>
                </ul>
              </div>
            </div>

            {/* Card 2 */}
            <div className="border-l-4 border-red-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">2️⃣ Total a Pagar</h3>
              <p className="text-sm text-muted-foreground">
                Mostra o total de despesas pendentes com fornecedores.
              </p>
              <div className="bg-red-50 p-3 rounded text-sm space-y-1">
                <p><strong>Inclui:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Contas manuais pendentes e atrasadas</li>
                  <li>Pedidos de compra não pagos</li>
                </ul>
              </div>
            </div>

            {/* Card 3 */}
            <div className="border-l-4 border-green-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">3️⃣ Total a Receber</h3>
              <p className="text-sm text-muted-foreground">
                Mostra o total de receitas pendentes de clientes.
              </p>
              <div className="bg-green-50 p-3 rounded text-sm space-y-1">
                <p><strong>Inclui:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Contas manuais pendentes e atrasadas</li>
                  <li>Pedidos de clientes não pagos</li>
                </ul>
              </div>
            </div>

            {/* Card 4 */}
            <div className="border-l-4 border-purple-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">4️⃣ Saldo Previsto</h3>
              <p className="text-sm text-muted-foreground">
                Calcula sua situação financeira futura.
              </p>
              <div className="bg-purple-50 p-3 rounded text-sm space-y-1">
                <p><strong>Fórmula:</strong></p>
                <p className="text-xs font-mono">Saldo Previsto = Total a Receber - Total a Pagar</p>
              </div>
            </div>

            {/* Filtros */}
            <div className="border-l-4 border-orange-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">🔍 Filtros</h3>
              <p className="text-sm text-muted-foreground">
                Use os filtros para personalizar a visualização dos dados.
              </p>
              <div className="bg-orange-50 p-3 rounded text-sm space-y-1">
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Período:</strong> Escolha entre períodos predefinidos ou customizados</li>
                  <li><strong>Tipo:</strong> Todos, Receitas ou Despesas</li>
                  <li><strong>Status:</strong> Pendente, Pago/Recebido, Atrasado ou Todos</li>
                  <li><strong>Busca:</strong> Procure por descrição, categoria ou observações</li>
                </ul>
              </div>
            </div>

            {/* Fluxo Diário */}
            <div className="border-l-4 border-indigo-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">📅 Fluxo de Caixa por Dia</h3>
              <p className="text-sm text-muted-foreground">
                Visualize todas as movimentações agrupadas por dia.
              </p>
              <div className="bg-indigo-50 p-3 rounded text-sm space-y-1">
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Cada card representa um dia com movimentações</li>
                  <li>Mostra receitas (verde) e despesas (vermelho) separadas</li>
                  <li>Badge colorida indica se o saldo do dia foi positivo ou negativo</li>
                </ul>
              </div>
            </div>

            {/* Gráfico */}
            <div className="border-l-4 border-teal-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">📊 Comparativo de Meses</h3>
              <p className="text-sm text-muted-foreground">
                Analise tendências comparando receitas, despesas e saldo ao longo dos meses.
              </p>
              <div className="bg-teal-50 p-3 rounded text-sm space-y-1">
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Barras Verdes:</strong> Receitas realizadas no mês</li>
                  <li><strong>Barras Vermelhas:</strong> Despesas pagas no mês</li>
                  <li><strong>Linha Roxa:</strong> Saldo líquido (receitas - despesas)</li>
                  <li>Selecione 6, 12 ou 24 meses para análise</li>
                  <li>Passe o mouse sobre o gráfico para ver valores exatos</li>
                </ul>
              </div>
            </div>

            {/* Configurar Contas e Formas */}
            <div className="border-l-4 border-purple-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">⚙️ Configurar Contas e Formas de Pagamento</h3>
              <p className="text-sm text-muted-foreground">
                Clique no botão "⚙️ Configurar" (no topo da página) para definir:
              </p>
              <div className="bg-purple-50 p-3 rounded text-sm space-y-1">
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Formas de Pagamento:</strong> PIX, Cartão, Dinheiro, etc</li>
                  <li><strong>Contas Bancárias:</strong> Caixa Loja, Banco Itaú, Mercado Pago, etc</li>
                </ul>
              </div>
              <p className="text-sm text-muted-foreground">
                Com isso, você consegue rastrear quanto recebeu em cada forma e onde o dinheiro está guardado!
              </p>
            </div>

            {/* Importação OFX/Excel */}
            <div className="border-l-4 border-cyan-500 pl-4 space-y-2">
              <h3 className="font-semibold text-lg">📊 Importar Transações</h3>
              <p className="text-sm text-muted-foreground">
                Clique no botão "Importar" (no topo) para importar transações de:
              </p>
              <div className="bg-cyan-50 p-3 rounded text-sm space-y-1">
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Arquivos OFX:</strong> Extratos bancários (Itaú, Bradesco, Caixa, etc)</li>
                  <li><strong>Planilhas Excel:</strong> Com colunas de Data, Descrição, Valor, Tipo</li>
                </ul>
              </div>
              <p className="text-sm text-muted-foreground">
                O sistema reconhece automaticamente receitas e despesas!
              </p>
            </div>

            {/* Dicas */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200 space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                💡 Dicas Úteis
              </h3>
              <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                <li>Revise o Fluxo de Caixa pelo menos 1x por semana para acompanhar sua saúde financeira</li>
                <li>Use o comparativo de meses para identificar padrões e tendências</li>
                <li>Configure o saldo inicial para reconciliar com seu banco</li>
                <li>O saldo atual será atualizado automaticamente quando marcar pagamentos como "pago" ou "recebido"</li>
                <li>Contas atrasadas sempre aparecem no fluxo, independente do período selecionado</li>
                <li>Clique em uma transação no "Fluxo de Caixa por Dia" para abrir os detalhes</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setIsAjudaModalOpen(false)}>
              Entendi, Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Importação */}
      <DialogImportarTransacoes
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["contas_receber"] });
          queryClient.invalidateQueries({ queryKey: ["contas_pagar"] });
          toast.success("Transações importadas com sucesso!");
        }}
      />

      {/* Dialog de Configuração */}
      <DialogConfigurarContasEFormas
        isOpen={isConfiguracaoModalOpen}
        onClose={() => setIsConfiguracaoModalOpen(false)}
      />
    </div>
  );
}

